import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';
import { useJsonLd, generateBreadcrumbSchema } from '@/utils/seo';

const COMICS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [currentPage, setCurrentPage] = useState(1);

  const currentCategory = categories.find((c) => c.slug === currentSlug) || categories[0];

  const allComics: Comic[] = getComicsByCategory(currentSlug);
  const totalPages = Math.ceil(allComics.length / COMICS_PER_PAGE);
  const displayedComics = allComics.slice(
    (currentPage - 1) * COMICS_PER_PAGE,
    currentPage * COMICS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSlug]);

  useEffect(() => {
    document.title = `${currentCategory.name}漫画 - 丫丫漫画免费在线阅读`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        `丫丫漫画${currentCategory.name}专区，海量${currentCategory.name}免费在线看。下拉式翻页阅读，流畅舒适的漫画体验。`
      );
    }
  }, [currentCategory]);

  const breadcrumbItems = [
    { name: '首页', url: '/' },
    { name: `${currentCategory.name}漫画` },
  ];

  const pageUrl = `https://jxjdxwx.cn/category/${currentSlug}`;
  const breadcrumbSchemaItems = [
    { name: '首页', url: 'https://jxjdxwx.cn/' },
    { name: `${currentCategory.name}漫画`, url: pageUrl },
  ];

  useJsonLd(generateBreadcrumbSchema(breadcrumbSchemaItems));

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-24 pb-6 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mt-4">
              {currentCategory.name}漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {allComics.length} 部漫画
            </p>
          </div>
        </div>

        <div className="px-4 md:px-6 py-2 bg-background-100 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <section className="px-4 md:px-6 py-10 bg-background-50 min-h-[400px]">
          <div className="max-w-7xl mx-auto">
            {displayedComics.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                  {displayedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:bg-primary-50 hover:border-primary-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => {
                          setCurrentPage(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer ${
                          currentPage === page
                            ? 'bg-primary-500 text-white'
                            : 'text-foreground-500 hover:bg-background-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:bg-primary-50 hover:border-primary-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 mb-4">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无该分类的漫画</p>
                <Link
                  to="/category/all"
                  className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看全部漫画
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}